import React, { ChangeEventHandler, useContext, useEffect, useState, memo } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Button } from 'transfix-ui/components/Button'
import { INLINE_MESSAGE_VARIANTS, InlineMessage } from 'transfix-ui/components/InlineMessage'
import { LogoTransfix } from 'transfix-ui/components/LogoTransfix'
import { TextField } from 'transfix-ui/components/TextField'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { StringParam } from 'use-query-params'
import { callApi, ENDPOINTS } from 'utils/api/callApi'
import { FP_HOME, FP_LOGIN } from 'apps/fleet-planner/config'
import { UserContext } from 'apps/fleet-planner/context'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { getTokenStatus } from 'apps/fleet-planner/utils/api/token'
import { Checkbox } from 'components/forms'
import { useQueryParams } from 'utils/hooks'
import { storeUserData } from 'utils/userAuth'
import { validatePassword } from 'utils/validators'

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: 280,
    [theme.breakpoints.up('md')]: {
      maxWidth: 420,
    },
  },
}))

export const HELPER_TEXT =
  'Include at least eight characters, that includes at least one uppercase letter, lowercase letter, a number, and a special symbol (!?@#$).'

interface IPasswordProps {
  invite?: boolean
}

export const Password = memo<IPasswordProps>(({ invite: hasInviteToken = false }) => {
  const { fetchUserData } = useContext(UserContext)
  const { trackEvent } = useFMSAnalytics()
  const classes = useStyles()
  const history = useHistory()
  const { token } = useParams<{ token: string }>()
  // eslint-disable-next-line camelcase
  const [{ reset_password: resetPassword }] = useQueryParams({ reset_password: StringParam })
  const [password, setPassword] = useState<string>()
  const [showPassword, setShowPassword] = useState<boolean>()
  const [error, setError] = useState<{ type: INLINE_MESSAGE_VARIANTS; message: string }>()

  const passwordChangeHandler: ChangeEventHandler<HTMLInputElement> = ({ target: { value } }) => {
    if (!validatePassword(value)) {
      setError({
        type: INLINE_MESSAGE_VARIANTS.ALERT,
        message: 'Your password does not meet the requirements below.',
      })
    } else {
      setError(undefined)
    }

    setPassword(value)
  }

  const getUserSessionDetails = async (id: number) => {
    try {
      const res = await callApi({
        endpoint: ENDPOINTS.v7Users,
        group: `${id}/session_details`,
        includeXHeaderUserId: false,
      })
      return res
    } catch (e) {
      setError({
        type: INLINE_MESSAGE_VARIANTS.ALERT,
        message: 'Something went wrong, please try again.',
      })
      return e
    }
  }

  const checkboxChangeHandler: ChangeEventHandler<HTMLInputElement> = ({ target: { checked } }) =>
    setShowPassword(checked)

  const onAuthSuccess = async (res: any) => {
    const { children = [] } = res
    // TODO: type with login res
    storeUserData({ ...res, app: 'fleet-planner' })
    if (children.length) {
      const userSessionDetailsRes = await getUserSessionDetails(children[0].id)
      await storeUserData({ ...userSessionDetailsRes, app: 'fleet-planner' })
    }
    await fetchUserData()
    history.push(FP_HOME)
  }

  // Based on that query param, we specify if this is a password reset or a confirmation
  const submitCreatePasswordHandler = async () => {
    try {
      if (!validatePassword(password)) {
        setError({
          type: INLINE_MESSAGE_VARIANTS.ALERT,
          message: 'Your password does not meet the requirements below.',
        })
      } else {
        setError(undefined)

        // Create password call
        const body = hasInviteToken
          ? { user: { invitationToken: token, password } }
          : {
              user: {
                [resetPassword ? 'resetPasswordToken' : 'confirmationToken']: token,
                password,
                passwordConfirmation: password,
              },
            }
        await callApi({
          endpoint: ENDPOINTS.password,
          method: 'PUT',
          body,
        })
        trackEvent('Login', 'CREATED_PASSWORD')

        if (hasInviteToken || resetPassword) {
          history.push(FP_LOGIN)
        } else {
          const res = await callApi({
            endpoint: ENDPOINTS.signIn,
            method: 'POST',
            body: {
              userLogin: { confirmationToken: token },
            },
          })
          await onAuthSuccess(res)
        }
      }
    } catch (e) {
      const message = e ?? 'Something went wrong. Please try again.'
      setError({ type: INLINE_MESSAGE_VARIANTS.ALERT, message })
    }
  }

  // Validates invite token parameters
  useEffect(() => {
    if (!hasInviteToken) return
    getTokenStatus(token).then(status => {
      if (status === 'expired') {
        history.replace(`${token}/reset`)
      } else if (status === 'invalid') {
        history.replace(`${token}/invalid`)
      }
    })
  }, [hasInviteToken, token, history])

  return (
    <Box display='flex' flexDirection='column' alignItems='center'>
      <Box mt={6}>
        <LogoTransfix color='black' />
      </Box>
      <Box mt={4.5} mb={3}>
        <Typography variant='h3'>
          {resetPassword ? 'Reset Password' : 'Create a Password'}
        </Typography>
      </Box>
      <Box className={classes.root}>
        {/* Password Input */}
        {error && (
          <InlineMessage className='mb5' fullWidth message={error.message} type={error.type} />
        )}
        <Box mb={1}>
          <TextField
            label='Password'
            placeholder='Enter a secure password'
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={passwordChangeHandler}
            helperText={HELPER_TEXT}
            error={error?.message === 'Your password does not meet the requirements below.'}
            dataTest='login-password-input'
            className='fs-exclude'
          />
        </Box>
        <Box mb={3} mt={2.5}>
          <Checkbox label='Show password' onChange={checkboxChangeHandler} />
        </Box>
        {/* Log In Button */}
        <Box mb={2}>
          <Button
            color='primary'
            fullWidth
            onClick={submitCreatePasswordHandler}
            size='large'
            disabled={!validatePassword(password)}
            data-test='request-link-submit'>
            Submit Password
          </Button>
        </Box>
        <Typography variant='caption' color='textSecondary' component='div'>
          By clicking &quot;Submit Password&quot; you agree to Transfix&apos;s{' '}
          <a href='https://transfix.io/terms-of-service/' target='_blank' rel='noopener noreferrer'>
            Terms and Conditions
          </a>{' '}
          and{' '}
          <a href='https://transfix.io/privacy-policy/' target='_blank' rel='noopener noreferrer'>
            Privacy Policy
          </a>
        </Typography>
      </Box>
    </Box>
  )
})
